import { redactValue, scrubBreadcrumbData, scrubTelemetryEvent } from "./telemetry-redaction.js";
// The telemetry debug settings and their production guard (CHT-770, Phase 13.4). Pure: it reads the `env` it is given.
//
//   LAFAYETTE_TELEMETRY_DEBUG = off | verbose | raw      (default `off`)
//
// - `off`: normal telemetry. Every export passes the shared redaction.
// - `verbose`: more ids and codes in the logs and breadcrumbs (development, test and staging). The redaction still runs.
// - `raw`: the redaction is bypassed so a developer can read a real payload. DEVELOPMENT AND TEST ONLY. Staging and production refuse it:
//   staging holds test accounts that are still real people's habits, and the injected-incident proof must read only allowed telemetry.
// - Production accepts only `off`. A bad value stops the start with a message that names the variable and the rule and never echoes a value.
//
// The same rule is applied to the names of the vendors: `sendDefaultPii` is never turned on by a setting. A setting cannot enable it.
export const TELEMETRY_DEBUG_ENV = "LAFAYETTE_TELEMETRY_DEBUG";
export const telemetryDebugModes = Object.freeze(["off", "verbose", "raw"]);
export class TelemetrySettingsError extends Error {
    code;
    variable;
    rule;
    constructor(code, rule) {
        super(`${TELEMETRY_DEBUG_ENV} is not valid: ${rule}`);
        this.name = "TelemetrySettingsError";
        this.code = code;
        this.variable = TELEMETRY_DEBUG_ENV;
        this.rule = rule;
    }
}
export function telemetrySettingsFromEnvironment(env, environment) {
    const text = (env[TELEMETRY_DEBUG_ENV] ?? "off").trim().toLowerCase();
    const value = text === "" ? "off" : text;
    const mode = telemetryDebugModes.find((candidate) => candidate === value);
    if (mode === undefined) {
        throw new TelemetrySettingsError("TELEMETRY_DEBUG_INVALID", "use off, verbose or raw");
    }
    if (environment === "production" && mode !== "off") {
        throw new TelemetrySettingsError("TELEMETRY_DEBUG_REFUSED_IN_PRODUCTION", "production accepts only off");
    }
    if (mode === "raw" && environment !== "development" && environment !== "test") {
        throw new TelemetrySettingsError("TELEMETRY_DEBUG_RAW_REFUSED_OUTSIDE_DEVELOPMENT", "raw is allowed only in development and test");
    }
    return { debug: mode, rawExport: mode === "raw", sendDefaultPii: false };
}
/** The settings of a surface that has no `env` (a mobile or admin bundle): debug is off unless the build says otherwise, and never in production. */
export function clientTelemetrySettings(debugFlag, environment) {
    return telemetrySettingsFromEnvironment({ [TELEMETRY_DEBUG_ENV]: debugFlag }, environment);
}
/**
 * The exporters of a surface for its settings: the shared redaction, or the identity in `raw` mode (which only development and test can
 * reach). Every Sentry hook, log writer and diagnostic of the API, the admin and the app asks for its exporters here, so there is one
 * place where the bypass exists and one test that proves production cannot reach it.
 */
export function telemetryExporters(settings) {
    if (settings.rawExport) {
        return {
            event: (event) => event,
            breadcrumb: (breadcrumb) => breadcrumb,
            value: (value) => value,
        };
    }
    return {
        event: scrubTelemetryEvent,
        breadcrumb: scrubBreadcrumbData,
        value: (value) => redactValue(value),
    };
}
